import {
  createFileRoute,
  notFound,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { getTemplate, listTemplates, useTemplate } from "@/lib/template-queries";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { SensitiveTemplateBanner } from "@/components/templates/sensitive-template-banner";
import { EmailCaptureBar } from "@/components/templates/email-capture-bar";
import { PastItemsFromTemplate } from "@/components/templates/past-items-from-template";
import { TemplateStandalonePage } from "@/components/templates/template-standalone-page";
import { hreflangLinks } from "@/lib/hreflang";

const searchSchema = z.object({
  drawer: z.coerce.number().optional(),
});

function canonicalFor(id: string) {
  return `https://checkbloom.app/templates/${id}`;
}

export const Route = createFileRoute("/templates/$id")({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const detail = await getTemplate(params.id);
    if (!detail) throw notFound();
    return detail;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Template not found — Checkbloom" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = canonicalFor(params.id);
    const title = `${loaderData.title} Checklist — Free to Use | Checkbloom`;
    const description =
      (loaderData.description?.trim() ||
        `${loaderData.title} checklist on Checkbloom.`).slice(0, 155);
    const ogImage = `https://checkbloom.app/api/public/og/template/${params.id}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(url)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: loaderData.title,
            description,
            datePublished: "2026-01-01",
            totalTime: `PT${Math.max(15, loaderData.items.length * 2)}M`,
            step: loaderData.items.map((it, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: it.title,
            })),
          }),
        },
      ],
    };
  },
  errorComponent: ({ error, reset }) => (
    <div className="p-8 text-center">
      <p className="text-sm">Couldn't load template.</p>
      <p className="mt-2 text-xs text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      <button className="mt-3 text-sm text-primary underline" onClick={reset}>
        Retry
      </button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center">Template not found.</div>
  ),
  component: TemplateRoute,
});

function TemplateRoute() {
  const detail = Route.useLoaderData() as import("@/lib/template-queries").TemplateDetail;
  const { id } = Route.useParams();
  const { drawer } = Route.useSearch();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    if (drawer) return;
    if (typeof window === 'undefined') return;
    const stored = sessionStorage.getItem('checkbloom_locale');
    if (stored && ['de', 'fr', 'nl', 'es'].includes(stored)) {
      sessionStorage.setItem('checkbloom_locale', stored);
      setRedirecting(true);
      navigate({
        to: '/templates/$id',
        params: { id },
        search: { drawer: 1 },
        replace: true,
      });
    }
  }, [drawer, id, navigate]);
  if (drawer) return <TemplateDrawer />;
  if (redirecting) return null;
  return (
    <TemplateStandalonePage
      detail={detail}
      canonicalUrl={canonicalFor(detail.id)}
    />
  );
}

function TemplateDrawer() {
  const { id } = Route.useParams();
  const detail = Route.useLoaderData() as import("@/lib/template-queries").TemplateDetail;
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();
  const [localePrefix, setLocalePrefix] = useState<string>('en');
  useEffect(() => {
    setLocalePrefix(sessionStorage.getItem('checkbloom_locale') ?? 'en');
  }, []);
  const [toasted, setToasted] = useState(false);
  const [acting, setActing] = useState(false);
  const closeRef = useRef<() => void>(() => {});
  closeRef.current = () => {
    const localeRoutes: Record<string, string> = {
      de: "/de/vorlagen",
      fr: "/fr/modeles",
      nl: "/nl/modellen",
      es: "/es/plantillas",
    };
    const savedLocale = sessionStorage.getItem("checkbloom_locale");
    const returnPath = savedLocale && localeRoutes[savedLocale]
      ? localeRoutes[savedLocale]
      : "/templates";
    navigate({ to: returnPath });
  };

  const sessionQ = useQuery({
    queryKey: ["session"],
    queryFn: async () => (await supabase.auth.getSession()).data.session,
    staleTime: 60_000,
  });
  const householdQ = useQuery({
    queryKey: ["my-household", sessionQ.data?.user?.id],
    enabled: !!sessionQ.data?.user?.id,
    queryFn: async () => {
      const uid = sessionQ.data!.user!.id;
      const { data } = await supabase
        .from("household_members")
        .select("household_id")
        .eq("user_id", uid)
        .limit(1)
        .maybeSingle();
      return data?.household_id as string | undefined;
    },
  });

  const listQ = useQuery({ queryKey: ["templates", "list"], queryFn: listTemplates });

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
      if (e.key === "ArrowLeft") goto(-1);
      if (e.key === "ArrowRight") goto(1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQ.data, id]);

  const ordered = useMemo(() => listQ.data ?? [], [listQ.data]);
  function goto(dir: 1 | -1) {
    const idx = ordered.findIndex((t) => t.id === id);
    if (idx < 0 || ordered.length === 0) return;
    const next = (idx + dir + ordered.length) % ordered.length;
    navigate({
      to: "/templates/$id",
      params: { id: ordered[next].id },
      search: { drawer: 1 },
    });
  }

  const [pastSelected, setPastSelected] = useState<string[]>([]);
  function togglePast(title: string, checked: boolean) {
    setPastSelected((prev) =>
      checked ? (prev.includes(title) ? prev : [...prev, title]) : prev.filter((t) => t !== title),
    );
  }

  const useMut = useMutation({
    mutationFn: async () => {
      if (!sessionQ.data) {
        navigate({ to: "/signup" });
        throw new Error("Sign in required");
      }
      if (!householdQ.data) {
        navigate({ to: "/onboarding/household" });
        throw new Error("Set up your household first");
      }
      const newId = await useTemplate(id, householdQ.data, sessionQ.data.user!.id);
      if (pastSelected.length > 0) {
        const { data: maxRow } = await supabase
          .from("checklist_items")
          .select("position")
          .eq("checklist_id", newId)
          .order("position", { ascending: false })
          .limit(1)
          .maybeSingle();
        const startPos = (maxRow?.position ?? -1) + 1;
        await supabase.from("checklist_items").insert(
          pastSelected.map((title, i) => ({
            checklist_id: newId,
            title,
            position: startPos + i,
            creator_id: sessionQ.data!.user!.id,
          })),
        );
      }
      return newId;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      router.navigate({ to: "/app/list/$id", params: { id: newId } });
    },
    onError: (e: Error) => {
      if (e.message !== "Sign in required" && e.message !== "Set up your household first") {
        toast.error(e.message);
      }
    },
  });

  const [firstChecked, setFirstChecked] = useState(false);
  function onItemTap() {
    if (!firstChecked) setFirstChecked(true);
    if (toasted) return;
    setToasted(true);
    toast(localePrefix === 'de' ? 'Verwenden Sie diese Vorlage, um Einträge abzuhaken →'
      : localePrefix === 'fr' ? 'Utilisez ce modèle pour commencer à cocher les éléments →'
      : localePrefix === 'nl' ? 'Gebruik dit sjabloon om items af te vinken →'
      : localePrefix === 'es' ? 'Usa esta plantilla para empezar a marcar elementos →'
      : 'Use this template to start checking items off →');
  }

  const d = detail;
  const isPremium = d.is_premium;
  const userTier: "free" | "premium" = "free";

  return (
    <>
      <div
        onClick={() => closeRef.current()}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      />
      <aside
        className="fixed z-50 bg-card shadow-2xl
                   inset-x-0 bottom-0 h-[85vh] rounded-t-3xl
                   md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-[420px] md:rounded-none md:rounded-l-3xl
                   flex flex-col
                   animate-in slide-in-from-bottom md:slide-in-from-right duration-300"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <button
            onClick={() => goto(-1)}
            aria-label={localePrefix === 'de' ? 'Vorherige Vorlage' : localePrefix === 'fr' ? 'Modèle précédent' : localePrefix === 'nl' ? 'Vorige sjabloon' : localePrefix === 'es' ? 'Plantilla anterior' : 'Previous template'}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            onClick={() => goto(1)}
            aria-label={localePrefix === 'de' ? 'Nächste Vorlage' : localePrefix === 'fr' ? 'Modèle suivant' : localePrefix === 'nl' ? 'Volgende sjabloon' : localePrefix === 'es' ? 'Plantilla siguiente' : 'Next template'}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"
          >
            <ChevronRight className="size-4" />
          </button>
          <div className="flex flex-1 items-center gap-2 truncate">
            <span className="text-xl">{d.emoji_icon}</span>
            <h2 className="truncate font-display text-base font-semibold text-foreground">
              {d.title}
            </h2>
          </div>
          <button
            onClick={() => closeRef.current()}
            aria-label={localePrefix === 'de' ? 'Schließen' : localePrefix === 'fr' ? 'Fermer' : localePrefix === 'nl' ? 'Sluiten' : localePrefix === 'es' ? 'Cerrar' : 'Close'}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <SensitiveTemplateBanner
            sensitiveTemplate={d.is_sensitive ?? false}
            className="mb-3"
          />
          <p className="text-sm text-muted-foreground">{d.description}</p>
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Badge
              variant="secondary"
              className={isPremium ? "bg-accent text-accent-foreground" : "bg-muted"}
            >
              {isPremium ? "Premium" : localePrefix === 'de' ? 'Kostenlos'
                : localePrefix === 'fr' ? 'Gratuit'
                : localePrefix === 'nl' ? 'Gratis'
                : localePrefix === 'es' ? 'Gratis'
                : 'Free'}
            </Badge>
            <span>
              {localePrefix === 'de' ? `${d.item_count} Einträge`
                : localePrefix === 'fr' ? `${d.item_count} éléments`
                : localePrefix === 'nl' ? `${d.item_count} items`
                : localePrefix === 'es' ? `${d.item_count} elementos`
                : `${d.item_count} items`}
            </span>
            {d.community_count >= 10 && (
              <span>
                ·{" "}
                {d.community_count >= 1000
                  ? localePrefix === 'de' ? `Vertrauensvoll von ${d.community_count.toLocaleString()}+ Haushalten genutzt`
                    : localePrefix === 'fr' ? `Utilisé par ${d.community_count.toLocaleString()}+ foyers`
                    : localePrefix === 'nl' ? `Vertrouwd door ${d.community_count.toLocaleString()}+ huishoudens`
                    : localePrefix === 'es' ? `Utilizado por ${d.community_count.toLocaleString()}+ hogares`
                    : `Trusted by ${d.community_count.toLocaleString()}+ households`
                  : localePrefix === 'de' ? `Von ${d.community_count} Haushalten verfeinert`
                    : localePrefix === 'fr' ? `Affiné par ${d.community_count} foyers`
                    : localePrefix === 'nl' ? `Verfijnd door ${d.community_count} huishoudens`
                    : localePrefix === 'es' ? `Refinado por ${d.community_count} hogares`
                    : `Refined by ${d.community_count} households`}
              </span>
            )}
          </div>

          {d.note && (
            <div
              className="mt-4 rounded-md border-l-2 border-primary bg-cream/60 px-3 py-2 text-[13px] italic text-muted-foreground"
              style={{ borderLeftWidth: 2 }}
            >
              {d.note}
            </div>
          )}

          <PastItemsFromTemplate
            templateId={id}
            userId={sessionQ.data?.user?.id}
            selectedTitles={pastSelected}
            onToggle={togglePast}
          />

          <ul className="mt-5 space-y-2">
            {d.items.map((it) => (
              <li
                key={it.id}
                onClick={onItemTap}
                className="flex cursor-default items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40"
              >
                <span className="size-4 shrink-0 rounded-full border border-foreground/40 opacity-40" />
                <span className="text-sm text-foreground/80">{it.title}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-border bg-card px-4 py-3">
          {isPremium && userTier === "free" && (
            <div className="mb-2 rounded-md bg-accent/15 px-3 py-2 text-xs text-foreground">
              {localePrefix === 'de' ? 'Premium-Vorlage — in Ihrer kostenlosen 7-Tage-Testversion enthalten'
                : localePrefix === 'fr' ? 'Modèle premium — inclus dans votre essai gratuit de 7 jours'
                : localePrefix === 'nl' ? 'Premium-sjabloon — inbegrepen in uw gratis proefperiode van 7 dagen'
                : localePrefix === 'es' ? 'Plantilla premium — incluida en tu prueba gratuita de 7 días'
                : 'Premium template — included in your free 7-day trial'}
            </div>
          )}
          <Button
            className="w-full"
            disabled={useMut.isPending || acting}
            onClick={() => {
              if (isPremium && userTier === "free") {
                track("upgrade_clicked", {
                  trigger_location: "premium_template",
                  plan_shown: "household_annual",
                  template_name: d.title,
                  user_id: sessionQ.data?.user?.id ?? null,
                  household_id: householdQ.data ?? null,
                  current_plan: userTier,
                });
              }
              setActing(true);
              useMut.mutate(undefined, { onSettled: () => setActing(false) });
            }}
          >
            {isPremium && userTier === "free"
              ? localePrefix === 'de' ? 'Kostenlose Testphase starten →'
                : localePrefix === 'fr' ? 'Démarrer l\'essai gratuit →'
                : localePrefix === 'nl' ? 'Gratis proefperiode starten →'
                : localePrefix === 'es' ? 'Iniciar prueba gratuita →'
                : 'Start free trial to use this →'
              : localePrefix === 'de' ? 'Vorlage kostenlos verwenden →'
              : localePrefix === 'fr' ? 'Utiliser ce modèle gratuitement →'
              : localePrefix === 'nl' ? 'Sjabloon gratis gebruiken →'
              : localePrefix === 'es' ? 'Usar esta plantilla gratis →'
              : 'Use this template →'}
          </Button>
        </div>
      </aside>
      <EmailCaptureBar
        triggerNow={firstChecked}
        isAuthenticated={!!sessionQ.data?.user?.id}
      />
    </>
  );
}
